import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { DomController, Platform } from '@ionic/angular';
import Hammer from 'hammerjs';

import { DrawerState } from './drawer-state';

@Component({
  selector: 'ion-bottom-drawer',
  templateUrl: './ion-bottom-drawer.component.html',
  styleUrls: ['./ion-bottom-drawer.component.scss'],
  standalone: false,
})
export class IonBottomDrawerComponent implements AfterViewInit, OnChanges {
  @Input() public dockedHeight = 50;
  @Input() public shouldBounce = true;
  @Input() public disableDrag = false;
  @Input() public distanceTop = 0;
  @Input() public transition = '0.25s ease-in-out';
  @Input() public state: DrawerState = DrawerState.Bottom;
  @Input() public minimumHeight = 0;

  @Output() public stateChange = new EventEmitter<DrawerState>();

  private startPositionTop = 0;
  private readonly bounceDelta = 30;

  constructor(
    private element: ElementRef,
    private renderer: Renderer2,
    private domCtrl: DomController,
    private platform: Platform,
  ) {}

  public ngAfterViewInit(): void {
    this.renderer.setStyle(
      this.element.nativeElement.querySelector(
        '.ion-bottom-drawer-scrollable-content :first-child'
      ),
      'touch-action',
      'none'
    );

    this.setDrawerState(this.state);

    const hammer = new Hammer(this.element.nativeElement);
    hammer.get('pan').set({ enable: true, direction: Hammer.DIRECTION_VERTICAL });
    hammer.on('pan panstart panend', (ev: any) => {
      if (this.disableDrag) {
        return;
      }

      switch (ev.type) {
        case 'panstart':
          this.handlePanStart();
          break;
        case 'panend':
          this.handlePanEnd(ev);
          break;
        default:
          this.handlePan(ev);
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.state) {
      return;
    }

    this.setDrawerState(changes.state.currentValue);
  }

  private setDrawerState(state: DrawerState): void {
    this.renderer.setStyle(this.element.nativeElement, 'transition', this.transition);

    switch (state) {
      case DrawerState.Bottom:
        this.setTranslateY('calc(100vh - ' + this.minimumHeight + 'px)');
        break;
      case DrawerState.Docked:
        this.setTranslateY(this.platform.height() - this.dockedHeight + 'px');
        break;
      default:
        this.setTranslateY(this.distanceTop + 'px');
    }
  }

  private handlePanStart(): void {
    this.startPositionTop = this.element.nativeElement.getBoundingClientRect().top;
  }

  private handlePanEnd(ev: any): void {
    if (this.shouldBounce && ev.isFinal) {
      this.renderer.setStyle(this.element.nativeElement, 'transition', this.transition);

      switch (this.state) {
        case DrawerState.Docked:
          this.handleDockedPanEnd(ev);
          break;
        case DrawerState.Top:
          this.handleTopPanEnd(ev);
          break;
        default:
          this.handleBottomPanEnd(ev);
      }
    }

    this.stateChange.emit(this.state);
  }

  private handleTopPanEnd(ev: any): void {
    if (ev.deltaY > this.bounceDelta) {
      this.state = DrawerState.Docked;
    } else {
      this.setTranslateY(this.distanceTop + 'px');
    }
  }

  private handleDockedPanEnd(ev: any): void {
    const absDeltaY = Math.abs(ev.deltaY);

    if (absDeltaY > this.bounceDelta && ev.deltaY < 0) {
      this.state = DrawerState.Top;
    } else if (absDeltaY > this.bounceDelta && ev.deltaY > 0) {
      this.state = DrawerState.Bottom;
    } else {
      this.setTranslateY(this.platform.height() - this.dockedHeight + 'px');
    }
  }

  private handleBottomPanEnd(ev: any): void {
    if (-ev.deltaY > this.bounceDelta) {
      this.state = DrawerState.Docked;
    } else {
      this.setTranslateY('calc(100vh - ' + this.minimumHeight + 'px)');
    }
  }

  private handlePan(ev: any): void {
    const pointerY = ev.center.y;
    this.renderer.setStyle(this.element.nativeElement, 'transition', 'none');

    if (pointerY > 0 && pointerY < this.platform.height()) {
      if (ev.additionalEvent === 'panup' || ev.additionalEvent === 'pandown') {
        const newTop = this.startPositionTop + ev.deltaY;

        if (newTop >= this.distanceTop) {
          this.setTranslateY(newTop + 'px');
        } else {
          this.setTranslateY(this.distanceTop + 'px');
        }

        if (newTop > this.platform.height() - this.minimumHeight) {
          this.setTranslateY(this.platform.height() - this.minimumHeight + 'px');
        }
      }
    }
  }

  private setTranslateY(value: string): void {
    this.domCtrl.write(() => {
      this.renderer.setStyle(this.element.nativeElement, 'transform', 'translateY(' + value + ')');
    });
  }
}
